// Shared display formatters for TP-Scout-Requests.

import { formatScoutPosition } from "../constants/scoutRequestChoices";

// Attribution date rule (DECISIONS D-005): relative under 24h ("2h ago", "just
// now"), absolute after ("12 Feb 2026"). One rule everywhere so cards never mix
// styles on the same surface.
export const formatScoutDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Always-absolute date ("12 Jan 2026") for "Posted by … · <date>" lines where the
// design shows the full date regardless of age.
export const formatScoutDateAbsolute = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Re-check pill month ("Re-check May 2027").
export const formatRecheckMonth = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

// Horizon display normaliser (T011). Horizon is stored as free text ("Summer
// 2027"), but legacy/API-seeded rows can carry a raw enum-ish token
// ("this_window"). Every horizon render point routes through this ONE helper:
// snake_case tokens are humanised ("this_window" → "This window"), real free
// text passes through untouched. Display-only — the filter bar and the edit
// form keep working on the stored value.
export const formatScoutHorizon = (value) => {
  const raw = (value == null ? "" : String(value)).trim();
  if (!raw) return "";
  // Only an unambiguous snake_case token (lowercase words joined by "_") is
  // rewritten; anything a human typed stays exactly as typed.
  if (!/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(raw)) return raw;
  const words = raw.split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// "Centre-back · 17–21 also Left-back" — the position + age-range line shared
// across cards. Word order per design (N-2): the secondary position trails the
// age range, not the primary position.
export const formatPositionAge = ({ position_primary, position_secondary, age_min, age_max }) => {
  const pos = formatScoutPosition(position_primary);
  let ages = "";
  if (age_min != null && age_max != null) ages = `${age_min}–${age_max}`;
  else if (age_min != null) ages = `${age_min}+`;
  else if (age_max != null) ages = `up to ${age_max}`;
  const head = [pos, ages].filter(Boolean).join(" · ");
  const secondary = position_secondary ? ` also ${formatScoutPosition(position_secondary)}` : "";
  return head + secondary;
};

// Compose the radar group title from the structured request_spec (D-011) so
// positions read in the design spelling ("Centre-back") and the age range is
// included — never the backend's raw display snapshot. Falls back to the frozen
// title only when the spec is absent (archived request whose row is gone).
export const formatRequestSpecTitle = (spec, fallbackTitle = "") => {
  if (!spec) return fallbackTitle || "";
  return [formatPositionAge(spec), formatScoutHorizon(spec.horizon)].filter(Boolean).join(" · ");
};

// The long-term origin line (design 6b: "from Centre-back · 17–21 (closed)").
// Same structured source; "(closed)" only when the source request is closed.
export const formatRequestSpecOrigin = (spec, fallbackTitle = "") => {
  if (!spec) return fallbackTitle ? `from ${fallbackTitle}` : "";
  return `from ${formatPositionAge(spec)}${spec.status === "closed" ? " (closed)" : ""}`;
};
